import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ArrowRightIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { LogoMark } from '../components/LogoMark';
import { SectionHeading } from '../components/SectionHeading';
import { FrameworkWheel } from '../components/FrameworkWheel';
import { FAQ } from '../sections/FAQ';
import { FRAMEWORK } from '../data/site';
interface FrameworkProps {
  onDownload: (title?: string) => void;
}
const SUBTITLES: Record<string, string> = {
  inputs: 'Collect Data',
  interpretation: 'Understand Geology',
  decisions: 'Create Value',
  feedback: 'Test Outcomes',
  improvements: 'Refine System',
  operating: 'People, Processes, Standards, Consistency'
};
const DEEP_DIVES: Record<string, {
  means: string;
  matters: string;
  breakdowns: string[];
  questions: string[];
}> = {
  inputs: {
    means: 'Inputs represent the collection of geological, operational and technical data that form the foundation of the mine geology system. This includes activities such as geological mapping, face sampling, drilling, QAQC, logging, observations and data capture. Inputs are the starting point of the system and strongly influence the quality of all downstream interpretation and decision-making.',
    matters: 'Poor or inconsistent inputs can introduce uncertainty, bias and operational risk that may only become visible later through reconciliation or system feedback.',
    breakdowns: ['Data arrives late or without context', 'Sampling and mapping standards drift between people', 'Assumptions go unrecorded and unchallenged'],
    questions: ['Do we know which inputs each decision actually depends on?', 'Would a new geologist trust this data without asking around?', 'Are assumptions written down where decisions are made?']
  },
  interpretation: {
    means: 'Interpretation is the process of turning raw geological and operational data into understanding and insight. This stage involves integrating information, identifying geological patterns, validating assumptions and developing confidence in geological models and interpretations. Interpretation is influenced not only by data quality, but also by geological experience, operational context and awareness of uncertainty.',
    matters: 'Strong interpretation helps improve decision-making, while weak or inconsistent interpretation can create downstream operational challenges throughout the system.',
    breakdowns: ['Two geologists, two models, one orebody', 'Model updates happen without review or challenge', 'Uncertainty is felt but never quantified or communicated'],
    questions: ['Is there a structured review before interpretation changes go live?', 'Can the team explain why the model changed?', 'Where is uncertainty highest, and does anyone downstream know?']
  },
  decisions: {
    means: 'Decisions are the operational and technical actions taken based on geological interpretation and available information. These decisions influence development priorities, drilling strategies, grade control, stope design, ore delineation, reconciliation outcomes and overall mine performance. The quality of decisions is directly linked to the strength of the inputs and interpretation that support them.',
    matters: 'Geological decisions are often made under operational pressure and uncertainty, making structured thinking, communication and awareness of risk critical to maintaining confidence and consistency within the system.',
    breakdowns: ['Decisions made on stale or unfit data because the dig cannot wait', 'The reasoning behind calls is never recorded', 'Risk is communicated informally, or not at all'],
    questions: ['Do we know which decisions matter most to value?', 'Is the basis for each major call captured anywhere?', 'Would we make the same decision again with the same information?']
  },
  feedback: {
    means: 'Feedback is the process of reviewing outcomes against expectations to identify gaps, validate assumptions and detect emerging issues within the system. This includes activities such as reconciliation, operational review, communication, performance assessment and reviewing geological outcomes against actual mining results.',
    matters: 'Effective feedback loops help identify where the system may be drifting, where assumptions may be incorrect and where improvements are required. Without strong feedback mechanisms, issues can persist and compound throughout the system over time.',
    breakdowns: ['Reconciliation happens but never reaches the geologist who made the call', 'Actuals vs expected reviewed monthly, decisions made daily', "Lessons live in someone's head, not the system"],
    questions: ['Does reconciliation actually change what we do next week?', 'Who closes the loop between outcome and decision-maker?', 'When did feedback last change a standard?']
  },
  improvements: {
    means: 'Improvements focus on the continuous refinement of mine geology systems through learning, adaptation and operational feedback. This stage involves identifying recurring weaknesses, implementing practical changes and strengthening system performance over time. Improvements may involve updating standards, refining workflows, improving communication, increasing consistency or addressing issues identified through reconciliation and feedback processes.',
    matters: 'The goal of improvement is not perfection, but the development of a stronger, more resilient geology system capable of continuous operational learning and adaptation.',
    breakdowns: ['Lessons noted in meetings, forgotten by the next campaign', 'Improvement depends on one motivated person', 'No rhythm — reviews only happen after something goes wrong'],
    questions: ['Is there a register where lessons become actions?', 'What is our improvement rhythm — weekly, monthly, never?', 'Who owns closing each loop?']
  },
  operating: {
    means: 'The Operating Layer represents the underlying structure that supports and influences the entire mine geology system. This includes people, processes, standards, consistency and the overall working environment in which geology systems operate. While often less visible than technical workflows, the operating layer strongly affects how effectively the system functions and how consistently geological practices are applied across teams and over time.',
    matters: 'Weak communication, unclear standards, inconsistent processes or poor operational alignment can significantly reduce the effectiveness of even technically strong geology systems. The operating layer provides the stability, structure and consistency required for the broader system to operate reliably and continuously improve.',
    breakdowns: ['Standards exist but practice in the field differs', 'Processes depend on individuals, not the team', 'Consistency breaks down across crews and rosters'],
    questions: ['Would the system survive your best geologist leaving?', 'Do standards describe what actually happens?', 'Is consistency checked, or assumed?']
  }
};
const CHECKLIST = ['Decisions can be traced back to the inputs they relied on', 'Interpretation changes go through a structured review', 'The reasoning behind major calls is recorded', 'Reconciliation results reach the people who made the calls', 'Actuals vs expected reviews happen on a regular rhythm', 'Lessons are captured in a register, not just remembered', 'Standards reflect what actually happens in the field', 'The system would survive a key person leaving', 'Improvement actions have owners and due dates'];
export function Framework({
  onDownload
}: FrameworkProps) {
  return <>
      <section className="relative w-full overflow-hidden bg-cream py-16 lg:py-24" data-id="element-281">
        <div className="mgs-grid absolute inset-0" aria-hidden data-id="element-282" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8" data-id="element-283">
          <div className="max-w-3xl" data-id="element-284">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-warmgray" data-id="element-285">
              <LogoMark className="h-3.5 w-3.5" data-id="element-286" /> The framework
            </p>
            <h1 className="mt-3 text-4xl font-black leading-[1.05] tracking-tight text-charcoal sm:text-5xl" data-id="element-287">
              The Mine Geology Systems Framework
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-warmgray" data-id="element-288">
              Five stages, continuously connected, held together by an operating
              layer of People, Processes, Standards and Consistency.
            </p>
          </div>

          <div className="mt-10 rounded-xl border-l-4 border-yellow bg-sand p-6 lg:p-8" data-id="element-289">
            <h2 className="text-lg font-black text-charcoal" data-id="element-290">
              What is Mine Geology Systems?
            </h2>
            <p className="mt-2 max-w-3xl text-base leading-relaxed text-charcoal/80" data-id="element-291">
              MGS is a practical framework for understanding and improving the
              way mine geology teams collect inputs, interpret information, make
              decisions, capture feedback and drive continuous improvement
              inside an operating layer of people, processes, standards and
              consistency.
            </p>
          </div>
        </div>
      </section>

      <section className="w-full bg-sand py-20 lg:py-24" data-id="element-292">
        <div className="mx-auto max-w-7xl px-5 lg:px-8" data-id="element-293">
          <div className="mx-auto max-w-[640px] rounded-2xl border border-charcoal/10 bg-cream p-6 shadow-card sm:p-8" data-id="element-294">
            <FrameworkWheel data-id="element-295" />
          </div>
        </div>
      </section>

      <section className="w-full bg-cream py-20 lg:py-28" data-id="element-296">
        <div className="mx-auto max-w-7xl px-5 lg:px-8" data-id="element-297">
          <SectionHeading eyebrow="Component deep dives" title="Each part of the system, in practice." body="What each component means, why it matters, where it commonly breaks down, and the questions worth asking on your own site." data-id="element-298" />
          <div className="mt-12 space-y-8" data-id="element-299">
            {FRAMEWORK.map((c, i) => {
            const d = DEEP_DIVES[c.key];
            return <motion.article key={c.key} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.45
            }} className="overflow-hidden rounded-2xl border border-charcoal/10 bg-sand shadow-card" data-id="element-300">
                  <div className="h-1.5 w-full" style={{
                backgroundColor: c.color
              }} aria-hidden data-id="element-301" />
                  <div className="p-6 lg:p-10" data-id="element-302">
                    <div className="flex items-center gap-4" data-id="element-303">
                      <span className="flex h-11 w-11 items-center justify-center rounded-lg text-base font-black text-white" style={{
                    backgroundColor: c.color
                  }} data-id="element-304">
                        {i < 5 ? i + 1 : '∞'}
                      </span>
                      <div data-id="element-305">
                        <h3 className="text-2xl font-black text-charcoal" data-id="element-306">
                          {c.name}
                        </h3>
                        <p className="text-sm font-bold uppercase tracking-wide" style={{
                      color: c.color
                    }} data-id="element-307">
                          {SUBTITLES[c.key]}
                        </p>
                      </div>
                    </div>
                    <div className="mt-6 grid gap-6 lg:grid-cols-2" data-id="element-308">
                      <div className="space-y-5" data-id="element-309">
                        <div data-id="element-310">
                          <h4 className="text-xs font-bold uppercase tracking-widest text-warmgray" data-id="element-311">
                            What it means
                          </h4>
                          <p className="mt-1.5 text-sm leading-relaxed text-charcoal/80" data-id="element-312">
                            {d.means}
                          </p>
                        </div>
                        <div data-id="element-313">
                          <h4 className="text-xs font-bold uppercase tracking-widest text-warmgray" data-id="element-314">
                            Why it matters
                          </h4>
                          <p className="mt-1.5 text-sm leading-relaxed text-charcoal/80" data-id="element-315">
                            {d.matters}
                          </p>
                        </div>
                      </div>
                      <div className="grid items-stretch gap-4 sm:grid-cols-2" data-id="element-316">
                        <div className="flex h-full flex-col rounded-xl border border-charcoal/10 bg-cream p-5" data-id="element-317">
                          <h4 className="text-xs font-bold uppercase tracking-widest text-warmgray" data-id="element-318">
                            Common breakdowns
                          </h4>
                          <ul className="mt-2.5 flex-1 space-y-2" data-id="element-319">
                            {d.breakdowns.map(b => <li key={b} className="flex items-start gap-2 text-sm leading-snug text-charcoal/80" data-id="element-320">
                                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" style={{
                            backgroundColor: c.color
                          }} data-id="element-321" />
                                {b}
                              </li>)}
                          </ul>
                        </div>
                        <div className="flex h-full flex-col rounded-xl border border-charcoal/10 bg-cream p-5" data-id="element-322">
                          <h4 className="text-xs font-bold uppercase tracking-widest text-warmgray" data-id="element-323">
                            Questions to ask
                          </h4>
                          <ul className="mt-2.5 flex-1 space-y-2" data-id="element-324">
                            {d.questions.map(q => <li key={q} className="flex items-start gap-2 text-sm leading-snug text-charcoal/80" data-id="element-325">
                                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" style={{
                            backgroundColor: c.color
                          }} data-id="element-326" />
                                {q}
                              </li>)}
                          </ul>
                        </div>
                      </div>
                    </div>
                    <Button to="/resources" variant="secondary" className="mt-6" data-id="element-327">
                      Find {c.name} resources
                      <ArrowRightIcon className="h-4 w-4" data-id="element-328" />
                    </Button>
                  </div>
                </motion.article>;
          })}
          </div>
        </div>
      </section>

      <section className="w-full bg-charcoal py-20 lg:py-28" data-id="element-329">
        <div className="mx-auto max-w-4xl px-5 lg:px-8" data-id="element-330">
          <h2 className="text-3xl font-black leading-tight text-cream sm:text-4xl" data-id="element-331">
            Is your mine geology system working?
          </h2>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-cream/80" data-id="element-332">
            Run through these honestly. Every unticked box is somewhere the
            system — not the geology — is costing you.
          </p>
          <ul className="mt-8 grid gap-3 sm:grid-cols-2" role="list" data-id="element-333">
            {CHECKLIST.map(item => <li key={item} className="flex items-start gap-3 rounded-xl border border-cream/15 bg-cream/5 p-4" data-id="element-334">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border border-yellow" data-id="element-335">
                  <CheckIcon className="h-3.5 w-3.5 text-yellow" data-id="element-336" />
                </span>
                <span className="text-sm leading-snug text-cream/90" data-id="element-337">
                  {item}
                </span>
              </li>)}
          </ul>
          <Button variant="primary" className="mt-8" onClick={() => onDownload()} data-id="element-338">
            Download the MGS Checklist — part of the free Toolkit
          </Button>
        </div>
      </section>

      <FAQ onDownload={() => onDownload()} data-id="element-339" />

      <section className="w-full bg-sand py-20 lg:py-24" data-id="element-340">
        <div className="mx-auto max-w-3xl px-5 text-center lg:px-8" data-id="element-341">
          <LogoMark className="mx-auto h-7 w-7" data-id="element-342" />
          <h2 className="mt-5 text-3xl font-black leading-tight text-charcoal sm:text-4xl" data-id="element-343">
            Take the framework with you.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-warmgray" data-id="element-344">
            The free MGS Toolkit walks through every component with practical
            questions for your own site.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row" data-id="element-345">
            <Button variant="primary" onClick={() => onDownload()} data-id="element-346">
              Download the Free MGS Toolkit
            </Button>
            <Button to="/insights" variant="secondary" data-id="element-347">
              Explore Insights
            </Button>
          </div>
        </div>
      </section>
    </>;
}